import {
	Background,
	Controls,
	type Edge,
	type Node,
	ReactFlow,
	ReactFlowProvider,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { ArrowLeft, Ban, ExternalLink, RotateCcw } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { fetchJson, sendJson } from "../../api/rest";
import { configSummary, useCatalogue } from "../../components/flows/catalogue";
import { type FlowNodeData, nodeTypes } from "../../components/flows/FlowNode";
import {
	btn,
	Empty,
	ErrorBanner,
	fmtDateTime,
	fmtNumber,
	Panel,
	Spinner,
	StatusChip,
} from "../../components/flows/ui";
import type { RunDetail, RunNode, TrafficSummary } from "../../types/flows";

// The canvas here is the run's own pinned definition, not the flow's current
// one: a flow edited after a run must still explain what that run did.

const LIVE = new Set(["queued", "running"]);

/**
 * Node output is stored keyed by output handle. Showing that object raw put a `{"out": [...]}`
 * wrapper in front of every item, which reads as part of the data — someone reasonably wrote
 * `{{ item.out[0].llm }}` after seeing it. A node with one output shows its items directly; one
 * with several labels each branch, which is the case where the handle is worth knowing.
 */
const OutputPanel = ({
	output,
	truncated,
}: {
	output: unknown;
	truncated: boolean;
}) => {
	const label = (
		<p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
			Output {truncated && <span className="normal-case">(truncated)</span>}
		</p>
	);

	if (!output || typeof output !== "object") {
		return (
			<div>
				{label}
				<p className="text-[12px] text-slate-400">Nothing recorded.</p>
			</div>
		);
	}

	const handles = Object.entries(output as Record<string, unknown>);

	if (handles.length === 1) {
		return (
			<div>
				{label}
				<JsonViewer value={handles[0][1]} />
			</div>
		);
	}

	return (
		<div>
			{label}
			<div className="space-y-2">
				{handles.map(([handle, items]) => (
					<div key={handle}>
						<p className="mb-1 font-mono text-[11px] text-slate-500">
							{handle}
						</p>
						<JsonViewer value={items} />
					</div>
				))}
			</div>
		</div>
	);
};

const JsonViewer = ({ value }: { value: unknown }) => {
	const [open, setOpen] = useState(false);
	const text = JSON.stringify(value, null, 2) ?? "null";
	const long = text.length > 600;

	return (
		<div>
			<pre className="max-h-96 overflow-auto rounded-lg bg-slate-900 p-3 font-mono text-[11px] leading-relaxed text-slate-100">
				{long && !open ? `${text.slice(0, 600)}\n…` : text}
			</pre>
			{long && (
				<button
					type="button"
					onClick={() => setOpen((value) => !value)}
					className="mt-1 text-[11px] font-medium text-primary"
				>
					{open ? "Show less" : `Show all ${fmtNumber(text.length)} characters`}
				</button>
			)}
		</div>
	);
};

const NodePanel = ({
	node,
	runId,
	onRetried,
}: {
	node: RunNode;
	runId: number;
	onRetried: (newRunId: number) => void;
}) => {
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const retry = async () => {
		setBusy(true);
		setError(null);
		try {
			const result = await sendJson<{ runId: number | null; reason?: string }>(
				`/flows/runs/${runId}/retry`,
				"POST",
				{ fromNodeId: node.nodeId },
			);
			if (result.runId) onRetried(result.runId);
			else setError(result.reason ?? "The retry did not start.");
		} catch (e) {
			setError(String(e));
		} finally {
			setBusy(false);
		}
	};

	const duration =
		node.startedAt && node.finishedAt
			? `${
					Math.round(
						(new Date(node.finishedAt).getTime() -
							new Date(node.startedAt).getTime()) /
							100,
					) / 10
				}s`
			: "—";

	return (
		<div className="space-y-4">
			<div className="flex flex-wrap items-center gap-2">
				<StatusChip status={node.status} kind="run" />
				<span className="text-[12px] text-slate-500">
					{fmtNumber(node.inputCount)} in · {fmtNumber(node.outputCount)} out ·{" "}
					{duration}
					{node.attempts > 1 && ` · ${node.attempts} attempts`}
				</span>
				<button
					type="button"
					onClick={retry}
					disabled={busy}
					className={`${btn.ghost} ml-auto`}
				>
					<RotateCcw className="h-4 w-4" /> Retry from here
				</button>
			</div>

			<ErrorBanner error={error ?? node.error} />

			{node.logs.length > 0 && (
				<div>
					<p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
						Logs
					</p>
					<ul className="space-y-0.5 rounded-lg bg-slate-50 p-3 font-mono text-[11px] text-slate-600">
						{node.logs.map((line) => (
							<li key={`${line.at}-${line.message}`}>
								<span className="text-slate-400">{fmtDateTime(line.at)}</span>{" "}
								{line.message}
							</li>
						))}
					</ul>
				</div>
			)}

			<OutputPanel output={node.output} truncated={node.truncated} />
		</div>
	);
};

const TrafficTab = ({ runId }: { runId: number }) => {
	const [traffic, setTraffic] = useState<TrafficSummary | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		fetchJson<TrafficSummary>(`/flows/runs/${runId}/traffic`)
			.then(setTraffic)
			.catch((e) => setError(String(e)));
	}, [runId]);

	if (error) return <ErrorBanner error={error} />;
	if (!traffic) return <Spinner />;

	if (traffic.publications.length === 0) {
		return (
			<Empty>
				This run published nothing, so there is no traffic to attribute. Only
				Facebook, Instagram, newsletter and CMS publish nodes record a
				publication.
			</Empty>
		);
	}

	return (
		<Panel
			title="What this run published"
			hint={`${fmtNumber(traffic.sessions)} sessions · ${fmtNumber(traffic.outboundClicks)} merchant clicks`}
		>
			<table className="w-full text-left text-[13px]">
				<thead>
					<tr className="border-b border-slate-200 text-slate-500">
						<th className="pb-2 pr-3 font-medium">Channel</th>
						<th className="pb-2 pr-3 font-medium">Title</th>
						<th className="pb-2 pr-3 font-medium">Published</th>
						<th className="pb-2 pr-3 font-medium">Sessions</th>
						<th className="pb-2 pr-3 font-medium">Merchant clicks</th>
						<th className="pb-2 font-medium">Impressions</th>
					</tr>
				</thead>
				<tbody>
					{traffic.publications.map((publication) => (
						<tr
							key={publication.id}
							className="border-b border-slate-100 last:border-0"
						>
							<td className="py-2 pr-3 text-slate-600">
								{publication.channel}
							</td>
							<td className="py-2 pr-3 text-slate-700">
								{publication.externalUrl ? (
									<a
										href={publication.externalUrl}
										target="_blank"
										rel="noreferrer"
										className="inline-flex items-center gap-1 text-primary"
									>
										{publication.title ?? "Untitled"}
										<ExternalLink className="h-3 w-3" />
									</a>
								) : (
									(publication.title ?? "Untitled")
								)}
							</td>
							<td className="py-2 pr-3 text-slate-500">
								{fmtDateTime(publication.publishedAt)}
							</td>
							<td className="py-2 pr-3 text-slate-600">
								{fmtNumber(publication.sessions)}
							</td>
							<td className="py-2 pr-3 text-slate-600">
								{fmtNumber(publication.outboundClicks)}
							</td>
							<td className="py-2 text-slate-500">
								{publication.impressions === null
									? "—"
									: fmtNumber(publication.impressions)}
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</Panel>
	);
};

const Inspector = () => {
	const { runId } = useParams();
	const navigate = useNavigate();
	// React Router gives every entry it pushed a key; "default" means this page was the first
	// thing loaded, so there is nothing to go back to.
	const canGoBack = useLocation().key !== "default";
	const { node: nodeDef, accentFor } = useCatalogue();
	const [run, setRun] = useState<RunDetail | null>(null);
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [tab, setTab] = useState<"canvas" | "traffic">("canvas");
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);

	const load = useCallback(async () => {
		try {
			setRun(await fetchJson<RunDetail>(`/flows/runs/${runId}`));
		} catch (e) {
			setError(String(e));
		}
	}, [runId]);

	useEffect(() => {
		load();
	}, [load]);

	// A run takes minutes and finishes without telling anyone, so the page polls
	// while it is live and stops the moment it is not.
	useEffect(() => {
		if (!run || !LIVE.has(run.status)) return;
		const timer = setInterval(load, 2000);
		return () => clearInterval(timer);
	}, [run, load]);

	const byNodeId = useMemo(
		() => new Map((run?.nodes ?? []).map((n) => [n.nodeId, n])),
		[run],
	);

	const nodes = useMemo<Node[]>(
		() =>
			(run?.definition.nodes ?? []).map((n) => {
				const def = nodeDef(n.type);
				const result = byNodeId.get(n.id);
				return {
					id: n.id,
					type: "flowNode",
					position: n.position,
					draggable: false,
					data: {
						def,
						label: n.data.label ?? n.type,
						summary: configSummary(def, n.data.config),
						accent: accentFor(n.type),
						runStatus: result?.status ?? "pending",
						inputCount: result?.inputCount ?? null,
						outputCount: result?.outputCount ?? null,
						readOnly: true,
					} satisfies FlowNodeData,
				};
			}),
		[run, nodeDef, accentFor, byNodeId],
	);

	const edges = useMemo<Edge[]>(
		() =>
			(run?.definition.edges ?? []).map((e) => ({
				id: e.id,
				source: e.source,
				sourceHandle: e.sourceHandle ?? undefined,
				target: e.target,
				targetHandle: e.targetHandle ?? undefined,
				animated: run ? LIVE.has(run.status) : false,
			})),
		[run],
	);

	const cancel = async () => {
		setBusy(true);
		try {
			await sendJson(`/flows/runs/${runId}/cancel`, "POST");
			await load();
		} catch (e) {
			setError(String(e));
		} finally {
			setBusy(false);
		}
	};

	if (!run) return error ? <ErrorBanner error={error} /> : <Spinner />;

	const selected = selectedId ? byNodeId.get(selectedId) : null;
	const selectedLabel = selectedId
		? (run.definition.nodes.find((n) => n.id === selectedId)?.data.label ??
			null)
		: null;

	return (
		<div className="flex h-[calc(100vh-8rem)] flex-col gap-3">
			<div className="flex flex-wrap items-center gap-2">
				{/* Back means back. This used to be a hard link to the runs list, so pressing Run now
				    in the editor and then Back dropped you somewhere you had never been. A run is
				    reached from the editor, the runs list and the queue, and the only thing that
				    knows which is the history — with the run's own flow as the fallback for a run
				    opened directly from a link. */}
				<button
					type="button"
					onClick={() =>
						canGoBack ? navigate(-1) : navigate(`/flows/${run.flowId}`)
					}
					className="rounded-md p-2 text-slate-400 hover:bg-canvas hover:text-slate-600"
					aria-label="Back"
					title="Back"
				>
					<ArrowLeft className="h-4 w-4" />
				</button>
				<div>
					<h1 className="text-lg font-bold text-slate-800">
						Run #{run.id} ·{" "}
						<Link to={`/flows/${run.flowId}`} className="text-primary">
							{run.flowName}
						</Link>
					</h1>
					<p className="flex flex-wrap items-center gap-1.5 text-[11px] text-slate-400">
						<StatusChip status={run.status} kind="run" />
						<span>v{run.flowVersion}</span>
						<span>· triggered by {run.trigger}</span>
						{run.triggeredBy && <span>({run.triggeredBy})</span>}
						{run.dryRun && (
							<span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-500">
								dry run — nothing left PriceOnline
							</span>
						)}
						<span>· started {fmtDateTime(run.startedAt ?? run.createdAt)}</span>
						{run.stats && (
							<span>
								·{" "}
								{Object.entries(run.stats)
									.filter(([, v]) => typeof v === "number")
									.map(([k, v]) => `${k} ${v}`)
									.join(", ")}
							</span>
						)}
					</p>
				</div>

				{LIVE.has(run.status) && (
					<button
						type="button"
						onClick={cancel}
						disabled={busy}
						className={`${btn.danger} ml-auto`}
					>
						<Ban className="h-4 w-4" /> Cancel
					</button>
				)}
			</div>

			<ErrorBanner error={error ?? run.error} />

			<div className="border-b border-slate-200">
				<nav className="flex gap-1">
					{(["canvas", "traffic"] as const).map((entry) => (
						<button
							key={entry}
							type="button"
							onClick={() => setTab(entry)}
							className={`border-b-2 px-4 py-2 text-[13px] font-medium capitalize transition-all ${
								tab === entry
									? "border-primary text-primary"
									: "border-transparent text-slate-500 hover:text-slate-700"
							}`}
						>
							{entry}
						</button>
					))}
				</nav>
			</div>

			{tab === "traffic" ? (
				<div className="overflow-y-auto">
					<TrafficTab runId={run.id} />
				</div>
			) : (
				<div className="flex min-h-0 flex-1 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
					<div className="min-w-0 flex-1">
						<ReactFlow
							nodes={nodes}
							edges={edges}
							nodeTypes={nodeTypes}
							onNodeClick={(_, node) => setSelectedId(node.id)}
							nodesConnectable={false}
							nodesDraggable={false}
							fitView
							proOptions={{ hideAttribution: true }}
						>
							<Background color="#e2e8f0" gap={18} />
							<Controls showInteractive={false} />
						</ReactFlow>
					</div>

					<div className="w-96 shrink-0 overflow-y-auto border-l border-slate-200 p-4">
						{selected ? (
							<>
								<h3 className="mb-3 text-[13px] font-semibold text-slate-800">
									{selected.label ?? selected.nodeType}
								</h3>
								<NodePanel
									node={selected}
									runId={run.id}
									onRetried={(id) => {
										window.location.href = `/flows/runs/${id}`;
									}}
								/>
							</>
						) : selectedId ? (
							// A node the run never reached has no `flow_run_nodes` row, which is
							// not the same as "nothing to show" — say which it is.
							<>
								<h3 className="mb-2 text-[13px] font-semibold text-slate-800">
									{selectedLabel ?? selectedId}
								</h3>
								<p className="text-[12px] leading-relaxed text-slate-500">
									This node never ran. The run{" "}
									{run.status === "failed"
										? "stopped before reaching it."
										: run.status === "waiting_approval"
											? "is paused upstream of it."
											: "did not reach it."}
								</p>
							</>
						) : (
							<p className="text-[12px] text-slate-400">
								Click a node to see what went in, what came out, and how long it
								took.
							</p>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

const FlowRun = () => (
	<ReactFlowProvider>
		<Inspector />
	</ReactFlowProvider>
);

export default FlowRun;
